import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Undo2 } from "lucide-react";
import { api, fmtMoney, type ReturnDoc, type ReturnTypeT } from "../api";
import { fmtDateTime } from "../lib/format";
import { usePageHeader } from "../lib/use-page-header";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mono } from "../components/ui/ledger";
import { Select } from "../components/ui/select";
import { Pagination } from "../components/ui/pagination";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { PageFilterPopover } from "../components/page-filter-popover";

const PAGE_SIZE = 30;

function docLabel(
  t: (k: string, o?: Record<string, unknown>) => string,
  r: ReturnDoc,
): string {
  return r.original_document_type === "purchase"
    ? t("qaytarishlar.doc.purchase", { id: r.original_document_id })
    : t("qaytarishlar.doc.sale", { id: r.original_document_id });
}

function TurBadge({ r }: { r: ReturnDoc }) {
  const { t } = useTranslation("ombor");
  return (
    <Badge variant={r.return_type === "sale_return" ? "secondary" : "outline"}>
      {t(`qaytarishlar.tur.${r.return_type}`)}
    </Badge>
  );
}

/**
 * Qaytarishlar (vozvrat) ro'yxati — sana/tur/hujjat/summa jadvali, tur va sana
 * filtri, qatorga bosilganda detal dialogi (tovarlar + zaxira/balans ta'siri).
 * Qaytarish yaratish shu yerda emas — Ombor sahifasidagi xarid/sotuv qatoridan.
 * Eslatma: filial filtri yo'q — /returns API branch bo'yicha filtrlamaydi.
 *
 * Bu — Ombor sahifasining 4-tabi (alohida menyu bandi emas), shuning uchun
 * `tovars` qorovuli bu yerda takrorlanmaydi: OmborPage allaqachon o'sha
 * feature'ga bog'langan.
 */
export function QaytarishlarPage({
  focusDoc,
}: {
  /**
   * Tovar jurnalidan kelingan qaytarish hujjati. Bu sahifa sana ustuni emas,
   * oddiy sahifalangan jadval, shuning uchun "topib berish" o'rniga to'g'ridan
   * to'g'ri detal oynasini ochamiz — hujjat sahifaning qayeridaligidan qat'i
   * nazar foydalanuvchi darhol uni ko'radi.
   */
  focusDoc?: { id: number; seq: number } | null;
} = {}) {
  const { t } = useTranslation(["ombor", "common"]);
  const [items, setItems] = useState<ReturnDoc[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typ, setTyp] = useState<"" | ReturnTypeT>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ReturnDoc | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .returns({
        return_type: typ || undefined,
        date_from: from || undefined,
        // Sana kiritish "kun" ma'nosida — kun oxirigacha qamrab olamiz
        // (backend `created_at <= date_to` deb solishtiradi).
        date_to: to ? `${to}T23:59:59` : undefined,
        page,
        page_size: PAGE_SIZE,
      })
      .then((pg) => {
        if (!alive) return;
        setItems(pg.items);
        setTotal(pg.total);
        setErr(null);
      })
      .catch((e) => {
        if (!alive) return;
        setErr((e as { message?: string }).message || t("common:common.error"));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [typ, from, to, page, t]);

  useEffect(() => {
    if (focusDoc) setDetailId(focusDoc.id);
    // `seq` ham bog'liqlikda — bir xil hujjat qayta bosilsa oyna yana ochilsin.
  }, [focusDoc?.id, focusDoc?.seq]);

  // Detal — ro'yxat javobida stok/balans ta'siri yo'q, alohida so'raymiz.
  useEffect(() => {
    if (detailId === null) {
      setDetail(null);
      return;
    }
    let alive = true;
    api
      .getReturn(detailId)
      .then((r) => alive && setDetail(r))
      .catch(() => alive && setDetailId(null));
    return () => {
      alive = false;
    };
  }, [detailId]);

  const filterCount = (typ ? 1 : 0) + (from ? 1 : 0) + (to ? 1 : 0);
  const clearFilters = () => {
    setTyp("");
    setFrom("");
    setTo("");
    setPage(1);
  };

  const filterNode = useMemo(
    () => (
      <PageFilterPopover count={filterCount} onClear={clearFilters}>
        <div className="space-y-1.5">
          <Label htmlFor="ret-tur">{t("qaytarishlar.filters.tur")}</Label>
          <Select
            id="ret-tur"
            value={typ}
            onChange={(e) => {
              setTyp(e.target.value as "" | ReturnTypeT);
              setPage(1);
            }}
          >
            <option value="">{t("qaytarishlar.filters.all")}</option>
            <option value="purchase_return">
              {t("qaytarishlar.tur.purchase_return")}
            </option>
            <option value="sale_return">
              {t("qaytarishlar.tur.sale_return")}
            </option>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ret-from">{t("qaytarishlar.filters.from")}</Label>
          <Input
            id="ret-from"
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ret-to">{t("qaytarishlar.filters.to")}</Label>
          <Input
            id="ret-to"
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </PageFilterPopover>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typ, from, to, filterCount, t],
  );

  usePageHeader({ filters: filterNode, filterCount });

  return (
    <div className="space-y-4">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Undo2 />}
              title={t("qaytarishlar.empty.title")}
              description={t("qaytarishlar.empty.desc")}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("qaytarishlar.table.sana")}</TableHead>
                  <TableHead>{t("qaytarishlar.table.tur")}</TableHead>
                  <TableHead>{t("qaytarishlar.table.hujjat")}</TableHead>
                  <TableHead>{t("qaytarishlar.table.mahsulotlar")}</TableHead>
                  <TableHead className="text-right">
                    {t("qaytarishlar.table.summa")}
                  </TableHead>
                  <TableHead>{t("qaytarishlar.table.sabab")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer"
                    onClick={() => setDetailId(r.id)}
                  >
                    <TableCell className="whitespace-nowrap">
                      <Mono className="text-muted-foreground">
                        {fmtDateTime(r.created_at)}
                      </Mono>
                    </TableCell>
                    <TableCell>
                      <TurBadge r={r} />
                    </TableCell>
                    <TableCell className="font-medium">
                      {docLabel(t, r)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {t("qaytarishlar.itemsCount", { count: r.items.length })}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {fmtMoney(r.jami_summa)}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {r.reason || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        {!loading && items.length > 0 && (
          <Pagination
            page={page}
            total={total}
            pageSize={PAGE_SIZE}
            onPage={setPage}
          />
        )}
      </Card>

      {detailId !== null && (
        <Dialog
          open
          onOpenChange={(o) => !o && setDetailId(null)}
          contentClassName="max-w-lg"
        >
          <div className="space-y-4 p-5">
            {detail === null ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : (
              <>
                <div>
                  <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
                    {t("qaytarishlar.detail.title", { id: detail.id })}
                  </h2>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {docLabel(t, detail)} · {fmtDateTime(detail.created_at)}
                  </p>
                </div>

                <div className="divide-y rounded-md border">
                  {detail.items.map((it) => (
                    <div
                      key={it.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">
                          {it.product_nomi}
                        </div>
                        <div className="text-xs text-muted-foreground tabular-nums">
                          {Number(it.quantity)} × {fmtMoney(it.unit_price)}
                        </div>
                      </div>
                      <span className="font-medium tabular-nums">
                        {fmtMoney(it.jami_summa)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-2.5 text-sm">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">
                      {t("qaytarishlar.detail.jami")}
                    </span>
                    <span className="font-bold tabular-nums">
                      {fmtMoney(detail.jami_summa)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">
                      {t("qaytarishlar.detail.stok")}
                    </span>
                    <span className="text-right">
                      {detail.stok_ozgarishi === "kirim"
                        ? t("qaytarishlar.detail.stokKirim")
                        : t("qaytarishlar.detail.stokChiqim")}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">
                      {t("qaytarishlar.detail.balans")}
                    </span>
                    <span className="tabular-nums">
                      {detail.balans_ozgarishi && Number(detail.balans_ozgarishi) > 0
                        ? `−${fmtMoney(detail.balans_ozgarishi)}`
                        : "—"}
                    </span>
                  </div>
                  {detail.reason && (
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">
                        {t("qaytarishlar.detail.sabab")}
                      </span>
                      <span className="text-right">{detail.reason}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}
